import type { TrustGateReasonCode, TrustGateResult } from "../../../shared/types";

type BadgeVariant = "success" | "warning" | "error" | "default" | "info";

export function trustGateLabel(trustGate: TrustGateResult | null | undefined): string {
  switch (trustGate?.state) {
    case "healthy":
      return "Decision-ready evidence";
    case "degraded":
      return "Degraded evidence";
    case "blocked":
      return "Blocked by trust gate";
    default:
      return "Trust state unavailable";
  }
}

export function trustGateVariant(trustGate: TrustGateResult | null | undefined): BadgeVariant {
  switch (trustGate?.state) {
    case "healthy":
      return "success";
    case "degraded":
      return "warning";
    case "blocked":
      return "error";
    default:
      return "default";
  }
}

function reasonLabel(code: TrustGateReasonCode): string {
  switch (code) {
    case "missing_source_url":
      return "Missing source URLs";
    case "partial_provenance":
      return "Partial provenance";
    case "stale_duplicate":
      return "Stale duplicates suppressed";
    case "rejected_evidence":
      return "Rejected evidence present";
    case "legacy_evidence_unrefreshed":
      return "Legacy evidence not refreshed";
    case "insufficient_decision_ready_evidence":
      return "No decision-ready evidence";
    default:
      return String(code).replace(/_/g, " ");
  }
}

export function trustGateReasonLabels(trustGate: TrustGateResult | null | undefined): string[] {
  return (trustGate?.reason_codes ?? []).map(reasonLabel);
}

export function trustGateSummary(trustGate: TrustGateResult | null | undefined): string {
  if (!trustGate) return "This surface does not have trust-gate data yet.";
  if (trustGate.state === "healthy") {
    return `${trustGate.decision_ready_candidates} decision-ready finding${trustGate.decision_ready_candidates === 1 ? "" : "s"} are backing this output.`;
  }
  if (trustGate.state === "blocked") {
    return "Do not treat this artifact as approval-ready. Regenerate or rerun upstream until at least one decision-ready source survives.";
  }
  return `${trustGate.decision_ready_candidates} decision-ready finding${trustGate.decision_ready_candidates === 1 ? "" : "s"} survived, but ${trustGate.degraded_candidates} finding${trustGate.degraded_candidates === 1 ? "" : "s"} are still degraded or legacy.`;
}

export function trustGateActionCopy(trustGate: TrustGateResult | null | undefined): string {
  if (!trustGate) return "Inspect the source run if this output looks suspect.";
  if (trustGate.state === "healthy") {
    return "Evidence is complete enough to review normally.";
  }
  const reasons = new Set(trustGate.reason_codes);
  if (reasons.has("insufficient_decision_ready_evidence")) {
    return "Hold approval and rerun the upstream scan or brief so fresh decision-ready evidence is attached.";
  }
  if (reasons.has("rejected_evidence")) {
    return "Inspect the source run, then rerun or regenerate so rejected evidence is replaced before approval.";
  }
  if (reasons.has("missing_source_url") || reasons.has("partial_provenance") || reasons.has("legacy_evidence_unrefreshed")) {
    return "Do not approve on provenance gaps alone. Inspect the source run if needed, then rerun or regenerate upstream so evidence is repaired.";
  }
  return "Inspect the source run, then rerun or regenerate upstream before treating this as approval-ready.";
}
